import './App.css';
import Layout from './components/Layout';
import { Route, Routes } from 'react-router-dom';
import { privateRoutes, publicRoutes, redirectRoutes } from './routes/Routes';
import PrivateRoute from './routes/PrivateRoutes.routes';
import { useContext, useEffect, useState } from 'react';
import { ArcaContext } from './context/ArcaContext.context';
import '@fontsource/space-grotesk';
import '@fontsource/inter';
import { useNavigate } from 'react-router-dom';
import getAccessToken from './api/thirdparty/payfit/getAccessToken';
import { Toaster } from './components/ui/sonner';
import { captureMagicLinkFromUrl } from './hooks/useMagicLinkContribution';
import DemoAccountBlockedNotice from './components/DemoAccountBlockedNotice';

const getRoutes = (routes: any) => {
	return routes.map((route: any, index: number) => (
		<Route key={index} path={route.path} element={route.component}>
			{route.children && getRoutes(route.children)}
		</Route>
	));
};

function usePayfitCallback() {
	const navigate = useNavigate();
	const { token: tokenContext } = useContext(ArcaContext);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const code = params.get('code');
		const state = params.get('state');

		if (!code) return;

		// Verificar que el state coincide con el almacenado en sessionStorage
		const storedState = sessionStorage.getItem('payfit_state');
		if (state !== storedState) {
			console.error('State mismatch - possible CSRF attack');
			return;
		}

		// Procesar el código de autorización en el backend
		getAccessToken(tokenContext, code)
			.then(() => {
				// Marcar como conectado
				localStorage.setItem('payfit_connected', Date.now().toString());

				// Limpiar el state de sessionStorage
				sessionStorage.removeItem('payfit_state');

				if (window.opener) {
					// Notificar a la ventana padre que la conexión fue exitosa
					window.opener.postMessage({ type: 'PAYFIT_CONNECTION_SUCCESS' }, window.location.origin);
					window.close();
				} else {
					window.history.replaceState({}, '', '/');
					window.close();
				}
			})
			.catch((error) => {
				console.error('Error during Payfit connection:', error);
				if (window.opener) {
					// Notificar error a la ventana padre
					window.opener.postMessage(
						{ type: 'PAYFIT_CONNECTION_ERROR', error: error.message },
						window.location.origin
					);
					window.close();
				}
			});
	}, [navigate, tokenContext]);
}

function App() {
	usePayfitCallback();
	// Enlace del correo con una acción (`?action=increase_contribution&amount=50&plan=…`): se
	// guarda en el PRIMER render, antes que cualquier efecto de react-router. Sin sesión, el
	// `<Navigate>` de `PrivateRoute` y el login borran la query antes de que monte nada de
	// detrás. Un `useEffect` aquí llegaría tarde: el de `<Navigate>` corre antes que el de su
	// padre. Lo consume el `Dashboard` (ver `useMagicLinkContribution`).
	useState(captureMagicLinkFromUrl);
	const { token: tokenContext } = useContext(ArcaContext);

	return (
		<>
			<Layout>
				<Routes>
					{getRoutes(redirectRoutes)}
					{getRoutes(publicRoutes)}
					<Route element={<PrivateRoute token={tokenContext} />}>{getRoutes(privateRoutes)}</Route>
					<Route path="/" element={<PrivateRoute token={tokenContext} />}></Route>
				</Routes>
			</Layout>
			{/* Aviso único de la cuenta demo de los revisores (comparsa docs/CUENTAS_DEMO.md). */}
			<DemoAccountBlockedNotice />
			{/* Los avisos salen arriba: en el iPhone, la isla tapaba su texto. Se suma la zona segura
			    a los 24/16 px que sonner deja por defecto (ordenador/móvil); en la web vale 0. */}
			<Toaster
				position="top-right"
				richColors
				offset={{ top: 'calc(24px + env(safe-area-inset-top, 0px))' }}
				mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top, 0px))' }}
			/>
		</>
	);
}

export default App;
